import React from "react"
import PropTypes from "prop-types"
import styled from "styled-components"
import Container from "@components/layout/container"
import Social from "@components/social"
import { Link } from "gatsby"
import Flex from "./elements/flex"
import { cssConfig } from "@utils/css-validator"
import Grid from "./elements/grid"
import { FaCanadianMapleLeaf } from "react-icons/fa"
import Span from "./elements/span"
import { ReactComponent as KlipfolioLogo } from "@images/klipfolio-logo-white.svg"

const StyledFooter = styled.footer`
  background-color: ${({ theme }) => theme.color.blue700};
  display: flex;
  flex-flow: column;
  width: 100%;
  margin-top: auto;
  bottom: 0;
  left: 0;

  dd,
  dl {
    margin: 0;
  }
`

const FooterLink = styled.a`
  color: ${({ theme }) => theme.color.white};
  text-decoration: none;
  font-weight: bold;
  font-size: 15px;
  line-height: 1;

  :hover {
    text-decoration: underline;
  }
`

const CategoryLink = styled(Link).withConfig(cssConfig)`
  color: ${({ theme }) => theme.color.white};
  :hover {
    text-decoration: ${({ noUnderline }) => !noUnderline && "underline"};
  }
  font-size: 1.1rem;
  line-height: 1.3;
`

const FooterLinkGrid = styled(FooterLink)`
  font-weight: normal;
  font-size: 1rem;
  line-height: 1.428571429;
  width: max-content;
`

const CopyrightContainer = styled.div`
  background-color: ${({ theme }) => theme.color.blue700};
`

const CopyrightRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  color: ${({ theme }) => theme.color.white};
  opacity: 0.8;
  padding: 1rem 0;
  gap: 1rem;
  line-height: 1.2;
  align-items: center;

  a {
    color: ${({ theme }) => theme.color.white};
    text-decoration: none;
  }

  .copy-links {
    display: flex;
    gap: 20px;
  }
`

const Footer = ({ footerLinks, copyright }) => {
  const year = new Date().getFullYear()

  return (
    <StyledFooter>
      <Grid
        margin="2rem auto"
        rows="masonry"
        columns="repeat(4, 1fr)"
        columnsMd="1fr 1fr"
        columnsSm="1fr"
        gap="2rem 0"
        width="100%"
        container
      >
        <Flex style={{ gridRow: "span 2" }} gap="1rem" alignItems="flex-start">
          <KlipfolioLogo height="40px" />
          <Flex
            data-remove="US"
            flexFlow="row"
            gap="0.5rem"
            background="white"
            alignItems="center"
            padding="0.4rem 0.7rem"
            borderRadius="0.5rem"
            margin="0 0 1rem"
          >
            <FaCanadianMapleLeaf size={20} style={{ color: "#FF0000" }} />
            <Span fontSize="1rem" color={"black"}>
              Made in <b>Canada</b>
            </Span>
          </Flex>
          <Flex gap="1rem" margin="0 0 1rem">
            {footerLinks?.top?.map(({ Link, URL }) => (
              <FooterLink key={URL} href={URL}>
                {Link}
              </FooterLink>
            ))}
          </Flex>
          <Flex flexFlow="row" gap="1rem">
            <Social.LinkedIn />
            <Social.Youtube />
          </Flex>
        </Flex>
        {footerLinks?.columns?.map(({ name, link, links }) => (
          <Flex as="dl" key={`column-footer-link-${link}`} gap="0.75rem">
            <CategoryLink as="a" href={link}>
              <dt>{name}</dt>
            </CategoryLink>
            <Flex gap="0.5rem">
              {links?.map(({ name, url }) => (
                <FooterLinkGrid key={url} href={url}>
                  <dd>{name}</dd>
                </FooterLinkGrid>
              ))}
            </Flex>
          </Flex>
        ))}
        <Flex flexFlow="column" gap="1rem">
          <CategoryLink as="a" href={`/resources`} style={{ fontWeight: 700 }}>
            <dt>Resources</dt>
          </CategoryLink>
          <Flex flexFlow="column" flexWrap="wrap" gap="0.5rem 1rem">
            {footerLinks?.resources?.map(({ Link, URL }) => (
              <FooterLinkGrid key={URL} href={URL}>
                <dd>{Link}</dd>
              </FooterLinkGrid>
            ))}
          </Flex>
        </Flex>
      </Grid>
      <CopyrightContainer>
        <Container>
          <CopyrightRow>
            <small className={"copy-links"}>
              {footerLinks?.copyright?.map(({ Link, URL }) => (
                <a key={URL} href={URL}>
                  {Link}
                </a>
              ))}
            </small>
            <small>
              Copyright © 2001 - {year} {copyright}
            </small>
          </CopyrightRow>
        </Container>
      </CopyrightContainer>
    </StyledFooter>
  )
}

Footer.propTypes = {
  footerLinks: PropTypes.object,
  copyright: PropTypes.string.isRequired,
}

Footer.defaultProps = {
  footerLinks: {},
  copyright: "",
}

export default Footer
